import React, { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import VoucherLogWarningsSummaryButton from '~/components/Vouchers/VoucherLogWarningsSummaryButton';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useProcessCreditGrantOrder } from '~/hooks/apis/promo/useProcessCreditGrantOrder';
import { useUnlogVoucher } from '~/hooks/apis/promo/useUnlogVoucher';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import { usePromoPreferences } from '~/hooks/usePromoPreferences';

import { ListVoucherLogsQuery, ProcessCreditGrantOrderResponse, VoucherLog } from '~/services/PromoService';

import { errorPayloadForDisplay } from '~/utils/creditGrantErrorPayload';
import { getVoucherLogDisplayMode } from '~/utils/voucherLogDisplayMode';
import { getVoucherLogWarningsSummary } from '~/utils/voucherLogWarnings';

import usePromoAdminPermission from '../Promo/hooks/usePromoPermission';

import {
  type CreditGrantApplyLastResult,
  type CreditGrantDryRunLastResult,
  buildVoucherLogColumns,
} from './voucherLogGridColumns';

type Props = {
  voucherId?: string;
  queryOptions?: ListVoucherLogsQuery;
  onQueryOptionsChange?: (queryOptions: ListVoucherLogsQuery) => void;
  onUserIdClick?: (userId: string) => void;
  onVoucherClick?: (voucherName: string) => void;
  pageSizeOptions?: Array<number>;
  /** Omit default top margin on the wrapper (e.g. when nested in an accordion). */
  flushTop?: boolean;
};

function toNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function getCreditGrantFailureReasons(
  data: ProcessCreditGrantOrderResponse | null | undefined,
  dryRun: boolean,
): Array<string> {
  const result = data?.result;
  if (!result) {
    return ['Missing process result'];
  }

  const reasons: Array<string> = [];
  const examined = toNumber(result.examined);
  const granted = toNumber(result.granted);
  const skippedOrderFetchError = toNumber(result.skippedOrderFetchError);
  const skippedTransitionalOrderStatus = toNumber(result.skippedTransitionalOrderStatus);
  const skippedVoucherValidationFailed = toNumber(result.skippedVoucherValidationFailed);
  const softDeletedIneligible = toNumber(result.softDeletedIneligible);

  if (examined <= 0) {
    reasons.push('No orders were examined');
  }
  if (!dryRun && granted <= 0) {
    reasons.push('No credits were granted');
  }
  if (skippedOrderFetchError > 0) {
    reasons.push(`${skippedOrderFetchError} order fetch error(s)`);
  }
  if (skippedTransitionalOrderStatus > 0) {
    reasons.push(`${skippedTransitionalOrderStatus} transitional order(s)`);
  }
  if (skippedVoucherValidationFailed > 0) {
    reasons.push(`${skippedVoucherValidationFailed} voucher validation failure(s)`);
  }
  if (softDeletedIneligible > 0) {
    reasons.push(`${softDeletedIneligible} ineligible soft-deleted order(s)`);
  }

  return reasons;
}

/** Default list query; exported so parents can mirror the table’s TanStack query key for summaries. */
export const VOUCHER_LOGS_TABLE_DEFAULT_QUERY: ListVoucherLogsQuery = {
  page: 0,
  limit: 20,
  sortBy: 'created_at',
  sortByDirection: 'desc',
};

const SORT_FIELD_TO_API_SORT_BY: Partial<Record<string, NonNullable<ListVoucherLogsQuery['sortBy']>>> = {
  createdAt: 'created_at',
  amount: 'amount',
  fkOrder: 'id_order',
};

const API_SORT_BY_TO_SORT_FIELD: Partial<Record<NonNullable<ListVoucherLogsQuery['sortBy']>, string>> = {
  created_at: 'createdAt',
  amount: 'amount',
  id_order: 'fkOrder',
};

export default function VoucherLogsTable({
  voucherId,
  queryOptions,
  onQueryOptionsChange,
  onUserIdClick,
  onVoucherClick,
  pageSizeOptions = [20, 50, 100],
  flushTop = false,
}: Props) {
  const [promoPreferences, setPromoPreferences] = usePromoPreferences();
  const voucherLogPreferences = promoPreferences.uiSelection.voucherLogs;
  const [internalQueryOptions, setInternalQueryOptions] = useState<ListVoucherLogsQuery>(
    VOUCHER_LOGS_TABLE_DEFAULT_QUERY,
  );
  const isControlled = queryOptions !== undefined;
  const brand = useCurrentBrand();
  let promoBrand = toPromoBrand(brand);
  if (!promoBrand) {
    console.warn(`Could not get promo brand for ${brand}`);
    promoBrand = 'luxuryescapes';
  }

  const resolvedQueryOptions = useMemo(() => {
    return {
      ...VOUCHER_LOGS_TABLE_DEFAULT_QUERY,
      ...(isControlled ? queryOptions : internalQueryOptions),
      ...(brand ? { brand: promoBrand } : {}),
      ...(voucherId ? { voucherId } : {}),
    };
  }, [internalQueryOptions, isControlled, queryOptions, voucherId, promoBrand, brand]);

  const updateQueryOptions = useCallback(
    (updater: (current: ListVoucherLogsQuery) => ListVoucherLogsQuery) => {
      const current = isControlled
        ? {
            ...VOUCHER_LOGS_TABLE_DEFAULT_QUERY,
            ...queryOptions,
          }
        : internalQueryOptions;
      const next = updater(current);

      if (!isControlled) {
        setInternalQueryOptions(next);
      }
      onQueryOptionsChange?.(next);
    },
    [internalQueryOptions, isControlled, onQueryOptionsChange, queryOptions],
  );

  const {
    data,
    isLoading,
    refetch: refetchVoucherLogs,
  } = useListVoucherLogs({
    query: resolvedQueryOptions,
  });

  const result = data?.result;
  const rows = useMemo(() => (result?.logs ?? []) as Array<VoucherLog>, [result?.logs]);
  const total = Number(data?.result?.total ?? 0);
  const displayMode = useMemo(() => getVoucherLogDisplayMode(rows), [rows]);
  const voucherLogWarnings = useMemo(() => getVoucherLogWarningsSummary(result), [result]);

  const isPromoAdmin = usePromoAdminPermission();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const unlogMutation = useUnlogVoucher();
  const processCreditGrantDryRunMutation = useProcessCreditGrantOrder();
  const processCreditGrantApplyMutation = useProcessCreditGrantOrder();
  const [unlogTarget, setUnlogTarget] = useState<VoucherLog | null>(null);
  const [creditGrantDryRunLoadingLogId, setCreditGrantDryRunLoadingLogId] = useState<string | null>(null);
  const [creditGrantDryRunReadyLogId, setCreditGrantDryRunReadyLogId] = useState<string | null>(null);
  const [creditGrantProcessingLogId, setCreditGrantProcessingLogId] = useState<string | null>(null);
  const [creditGrantLastResult, setCreditGrantLastResult] = useState<CreditGrantApplyLastResult | null>(null);
  const [creditGrantDryRunLastResult, setCreditGrantDryRunLastResult] = useState<CreditGrantDryRunLastResult | null>(
    null,
  );

  const unlogOrderLogsQuery = useMemo<ListVoucherLogsQuery | undefined>(() => {
    if (!unlogTarget?.fkOrder) {
      return undefined;
    }
    return {
      orderId: unlogTarget.fkOrder,
      page: 0,
      limit: 100,
      sortBy: 'created_at',
      sortByDirection: 'desc',
      includeDeleted: true,
    };
  }, [unlogTarget?.fkOrder]);

  const { data: sameOrderLogsResponse, isLoading: sameOrderLogsLoading } = useListVoucherLogs({
    query: unlogOrderLogsQuery,
    enabled: Boolean(unlogOrderLogsQuery),
  });

  const sameOrderLogsTotal = Number(sameOrderLogsResponse?.result?.total ?? 0);

  const otherVoucherLogsForOrder = useMemo(() => {
    if (!unlogTarget) {
      return [];
    }
    const logs = (sameOrderLogsResponse?.result?.logs ?? []) as Array<VoucherLog>;
    return logs.filter((log) => log.voucherLogId !== unlogTarget.voucherLogId);
  }, [sameOrderLogsResponse, unlogTarget]);

  const otherLogVoucherIds = useMemo(
    () =>
      Array.from(
        new Set(otherVoucherLogsForOrder.map((log) => log.voucherId).filter((id): id is string => Boolean(id))),
      ).sort(),
    [otherVoucherLogsForOrder],
  );

  const { data: otherLogsVouchersMap } = useGetVouchersByIds({
    voucherIds: otherLogVoucherIds,
    brand: brand ?? undefined,
    enabled: Boolean(unlogTarget) && otherLogVoucherIds.length > 0,
  });
  const resolvedOtherLogsVouchersMap = useMemo(() => otherLogsVouchersMap ?? new Map(), [otherLogsVouchersMap]);

  const otherVoucherLogsDialogDisplayMode = useMemo(
    () => getVoucherLogDisplayMode(otherVoucherLogsForOrder),
    [otherVoucherLogsForOrder],
  );

  const otherLogsUserIds = useMemo(() => {
    const ids = otherVoucherLogsForOrder.flatMap((log) =>
      [log.createdBy, log.userId].filter((id): id is string => Boolean(id)),
    );
    return Array.from(new Set(ids)).sort();
  }, [otherVoucherLogsForOrder]);

  const { data: otherLogsUsersSummaryMap } = useGetUsersSummaryByIds({
    userIds: otherLogsUserIds,
    brand,
    enabled: Boolean(unlogTarget) && otherLogsUserIds.length > 0,
  });
  const resolvedOtherLogsUsersSummaryMap = useMemo(
    () => otherLogsUsersSummaryMap ?? new Map(),
    [otherLogsUsersSummaryMap],
  );

  const creditGrantMutationBusy =
    processCreditGrantDryRunMutation.isPending || processCreditGrantApplyMutation.isPending;

  const handleCreditGrantDryRun = useCallback(
    (log: VoucherLog) => {
      if (!isPromoAdmin) {
        enqueueSnackbar('Promo Admin role is required to process credit grants.', { variant: 'warning' });
        return;
      }
      const orderId = log.fkOrder ?? log.fk_order;
      if (orderId == null || String(orderId).trim() === '') {
        return;
      }
      const logId = String(log.voucherLogId ?? '');
      processCreditGrantApplyMutation.reset();
      setCreditGrantLastResult(null);
      setCreditGrantDryRunLastResult(null);
      setCreditGrantDryRunReadyLogId(null);
      setCreditGrantDryRunLoadingLogId(logId);
      processCreditGrantDryRunMutation.mutate(
        { orderIds: [String(orderId)], dryRun: true },
        {
          onSuccess: (data) => {
            const failureReasons = getCreditGrantFailureReasons(data, true);
            if (failureReasons.length > 0) {
              setCreditGrantDryRunLastResult(null);
              setCreditGrantDryRunReadyLogId(null);
              enqueueSnackbar(`Dry run failed: ${failureReasons.join(', ')}`, { variant: 'error' });
              return;
            }
            setCreditGrantDryRunReadyLogId(logId);
            setCreditGrantDryRunLastResult({ voucherLogId: logId, data });
          },
          onError: (error) => {
            enqueueSnackbar(`Dry run failed: ${error instanceof Error ? error.message : String(error)}`, {
              variant: 'error',
            });
          },
          onSettled: () => {
            setCreditGrantDryRunLoadingLogId(null);
          },
        },
      );
    },
    [enqueueSnackbar, isPromoAdmin, processCreditGrantApplyMutation, processCreditGrantDryRunMutation],
  );

  const handleCreditGrantApply = useCallback(
    (log: VoucherLog) => {
      const orderId = log.fkOrder ?? log.fk_order;
      if (orderId == null || String(orderId).trim() === '') {
        return;
      }
      const logId = String(log.voucherLogId ?? '');
      setCreditGrantDryRunReadyLogId(null);
      setCreditGrantProcessingLogId(logId);
      processCreditGrantApplyMutation.mutate(
        { orderIds: [String(orderId)], dryRun: false },
        {
          onSuccess: async (data) => {
            const failureReasons = getCreditGrantFailureReasons(data, false);
            if (failureReasons.length > 0) {
              setCreditGrantLastResult({
                voucherLogId: logId,
                outcome: 'error',
                error: {
                  message: 'Credit grant was not applied successfully',
                  reasons: failureReasons,
                  response: data,
                },
              });
              enqueueSnackbar(`Credit grant failed: ${failureReasons.join(', ')}`, { variant: 'error' });
              return;
            }
            enqueueSnackbar('Credit grant applied successfully.', { variant: 'success' });
            await refetchVoucherLogs();
            setCreditGrantDryRunLastResult(null);
            setCreditGrantLastResult({ voucherLogId: logId, outcome: 'success', data });
          },
          onError: (error) => {
            setCreditGrantLastResult({
              voucherLogId: logId,
              outcome: 'error',
              error: errorPayloadForDisplay(error),
            });
            enqueueSnackbar(`Credit grant failed: ${error instanceof Error ? error.message : String(error)}`, {
              variant: 'error',
            });
          },
          onSettled: () => {
            setCreditGrantProcessingLogId(null);
          },
        },
      );
    },
    [enqueueSnackbar, processCreditGrantApplyMutation, refetchVoucherLogs],
  );

  const handleUnlogConfirm = useCallback(async () => {
    if (!unlogTarget) {
      return;
    }
    try {
      await unlogMutation.mutateAsync({
        fkOrder: unlogTarget.fkOrder,
        specificVoucherUserId: unlogTarget.voucherUserId,
      });
      enqueueSnackbar('Voucher log removed successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-logs'] });
    } catch (error) {
      enqueueSnackbar(`Failed to unlog voucher: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setUnlogTarget(null);
    }
  }, [enqueueSnackbar, queryClient, unlogMutation, unlogTarget]);
  const uniqueUserIds = useMemo(() => {
    const ids = rows.flatMap((log) => [log.createdBy, log.userId].filter((id): id is string => Boolean(id)));
    return Array.from(new Set(ids)).sort();
  }, [rows]);
  const { data: usersSummaryMap } = useGetUsersSummaryByIds({ userIds: uniqueUserIds, brand });
  const resolvedUsersSummaryMap = useMemo(() => usersSummaryMap ?? new Map(), [usersSummaryMap]);

  const uniqueVoucherIds = useMemo(() => {
    return Array.from(new Set(rows.map((log) => log.voucherId).filter((id): id is string => Boolean(id)))).sort();
  }, [rows]);
  const { data: vouchersMap } = useGetVouchersByIds({ voucherIds: uniqueVoucherIds, brand: brand ?? undefined });
  const resolvedVouchersMap = useMemo(() => vouchersMap ?? new Map(), [vouchersMap]);

  const columns = useMemo(
    () =>
      buildVoucherLogColumns({
        usersSummaryMap: resolvedUsersSummaryMap,
        vouchersMap: resolvedVouchersMap,
        onUserIdClick,
        onVoucherClick,
        onUnlog: setUnlogTarget,
        unlogPending: unlogMutation.isPending,
        onProcessCreditGrant: handleCreditGrantDryRun,
        onApplyCreditGrant: handleCreditGrantApply,
        processCreditGrantAllowed: isPromoAdmin,
        processCreditGrantPending: creditGrantMutationBusy,
        creditGrantDryRunLoadingLogId,
        creditGrantDryRunReadyLogId,
        creditGrantProcessingLogId,
        creditGrantLastResult,
        creditGrantDryRunLastResult,
        displayMode,
      }),
    [
      resolvedUsersSummaryMap,
      resolvedVouchersMap,
      onUserIdClick,
      onVoucherClick,
      unlogMutation.isPending,
      displayMode,
      isPromoAdmin,
      handleCreditGrantDryRun,
      handleCreditGrantApply,
      creditGrantMutationBusy,
      creditGrantDryRunLoadingLogId,
      creditGrantDryRunReadyLogId,
      creditGrantProcessingLogId,
      creditGrantLastResult,
      creditGrantDryRunLastResult,
    ],
  );

  const otherOrderLogsColumns = useMemo(
    () =>
      buildVoucherLogColumns({
        usersSummaryMap: resolvedOtherLogsUsersSummaryMap,
        vouchersMap: resolvedOtherLogsVouchersMap,
        onUserIdClick,
        onVoucherClick,
        includeUnlogColumn: false,
        includeProcessCreditGrantColumn: false,
        displayMode: otherVoucherLogsDialogDisplayMode,
      }),
    [
      resolvedOtherLogsUsersSummaryMap,
      resolvedOtherLogsVouchersMap,
      onUserIdClick,
      onVoucherClick,
      otherVoucherLogsDialogDisplayMode,
    ],
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: resolvedQueryOptions.page ?? VOUCHER_LOGS_TABLE_DEFAULT_QUERY.page ?? 0,
      pageSize: resolvedQueryOptions.limit ?? VOUCHER_LOGS_TABLE_DEFAULT_QUERY.limit ?? 20,
    }),
    [resolvedQueryOptions.limit, resolvedQueryOptions.page],
  );

  const sortModel = useMemo<GridSortModel>(() => {
    if (!resolvedQueryOptions.sortBy && voucherLogPreferences.sortModel.length > 0) {
      return voucherLogPreferences.sortModel;
    }
    if (!resolvedQueryOptions.sortBy) {
      return [];
    }
    const sortField = API_SORT_BY_TO_SORT_FIELD[resolvedQueryOptions.sortBy];
    if (!sortField) {
      return [];
    }

    return [
      {
        field: sortField,
        sort: resolvedQueryOptions.sortByDirection ?? 'asc',
      },
    ];
  }, [resolvedQueryOptions.sortBy, resolvedQueryOptions.sortByDirection, voucherLogPreferences.sortModel]);

  const handlePaginationModelChange = useCallback(
    (nextModel: GridPaginationModel) => {
      updateQueryOptions((current) => ({
        ...current,
        page: nextModel.page,
        limit: nextModel.pageSize,
      }));
    },
    [updateQueryOptions],
  );

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      const nextSort = nextSortModel[0];
      if (!nextSort?.sort) {
        setPromoPreferences((current) => ({
          ...current,
          uiSelection: {
            ...current.uiSelection,
            voucherLogs: { ...current.uiSelection.voucherLogs, sortModel: [] },
          },
        }));
        updateQueryOptions((current) => ({
          ...current,
          sortBy: undefined,
          sortByDirection: undefined,
        }));
        return;
      }

      const nextSortBy = SORT_FIELD_TO_API_SORT_BY[nextSort.field];
      if (!nextSortBy) {
        return;
      }

      updateQueryOptions((current) => ({
        ...current,
        sortBy: nextSortBy,
        sortByDirection: nextSort.sort === 'desc' ? 'desc' : 'asc',
      }));
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherLogs: { ...current.uiSelection.voucherLogs, sortModel: nextSortModel },
        },
      }));
    },
    [setPromoPreferences, updateQueryOptions],
  );

  const handleFilterChange = useCallback(
    (field: keyof ListVoucherLogsQuery, value: unknown) => {
      updateQueryOptions((current) => ({
        ...current,
        [field]: value || undefined,
        page: 0,
      }));
    },
    [updateQueryOptions],
  );

  return (
    <Box mt={flushTop ? 0 : 3}>
      <PageSubheader title={`Voucher Logs (${total})`}>
        <VoucherLogWarningsSummaryButton warningSummaries={voucherLogWarnings} />
      </PageSubheader>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          label="User ID"
          size="small"
          value={resolvedQueryOptions.userId ?? ''}
          onChange={(e) => handleFilterChange('userId', e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <TextField
          label="Order ID"
          size="small"
          value={resolvedQueryOptions.orderId ?? ''}
          onChange={(e) => handleFilterChange('orderId', e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={resolvedQueryOptions.includeDeleted ?? false}
              onChange={(e) => handleFilterChange('includeDeleted', e.target.checked || undefined)}
            />
          }
          label="Include Deleted"
        />
      </Stack>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row) => row.voucherLogId}
        loading={isLoading}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        columnVisibilityModel={{
          currencyCode: false,
          voucherLogId: false,
          voucherUserId: false,
          deletedAt: !!resolvedQueryOptions.includeDeleted,
        }}
        pageSizeOptions={pageSizeOptions}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
        getRowClassName={(params) => {
          const id = String(params.id);
          if (creditGrantProcessingLogId != null && id === String(creditGrantProcessingLogId)) {
            return 'voucher-log-row-credit-grant-loading';
          }
          if (creditGrantDryRunLoadingLogId != null && id === String(creditGrantDryRunLoadingLogId)) {
            return 'voucher-log-row-credit-grant-loading';
          }
          return '';
        }}
        sx={{
          backgroundColor: 'background.paper',
          width: '100%',
          maxWidth: '100%',
          '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', lineHeight: 1.2 },
          '& .MuiDataGrid-cell': { fontSize: '0.8125rem' },
          '& .MuiDataGrid-row.voucher-log-row-credit-grant-loading': {
            opacity: 0.72,
          },
        }}
      />

      <Dialog open={Boolean(unlogTarget)} onClose={() => setUnlogTarget(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Unlog Voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove the voucher log for order <strong>{unlogTarget?.fkOrder}</strong>? This will remove the use of this
            voucher and allow the customer to use the voucher again. Use sparingly to correct problems with voucher
            usage or to correct errors in voucher data (and please raise a bug ticket) This unlog is triggered when the
            ORDER_CANCELLED & ORDER_PAYMENT_FAILED events are triggered and we expect these events should capture most
            cases of the unlog being required.
          </Typography>
          {unlogTarget?.voucherUserId && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Voucher User ID: {unlogTarget.voucherUserId}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Other voucher logs for this order
            </Typography>
            {sameOrderLogsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!sameOrderLogsLoading && otherVoucherLogsForOrder.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No other voucher logs for this order.
              </Typography>
            )}
            {!sameOrderLogsLoading && otherVoucherLogsForOrder.length > 0 && (
              <>
                {sameOrderLogsTotal > 100 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Showing up to 100 logs; this order has {sameOrderLogsTotal} in total.
                  </Typography>
                )}
                <Box sx={{ width: '100%', height: 320, mt: 0.5 }}>
                  <DataGrid
                    rows={otherVoucherLogsForOrder}
                    columns={otherOrderLogsColumns}
                    getRowId={(row) => row.voucherLogId}
                    hideFooter
                    disableRowSelectionOnClick
                    density="compact"
                    columnVisibilityModel={{
                      currencyCode: false,
                      voucherLogId: false,
                      voucherUserId: false,
                      deletedAt: true,
                    }}
                    sx={{
                      backgroundColor: 'background.paper',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setUnlogTarget(null)} disabled={unlogMutation.isPending}>
            Cancel
          </Button>
          {isPromoAdmin && (
            <Button variant="contained" color="primary" onClick={handleUnlogConfirm} disabled={unlogMutation.isPending}>
              Unlog
            </Button>
          )}
          {!isPromoAdmin && (
            <Alert severity="warning">You need to have the 'Promo Admin' role to unlog vouchers.</Alert>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
